/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from "react";
import { css } from "@emotion/react";
import DOMPurify from "dompurify";
import { HighlightedText } from "@components/";

const ReadMoreHtmlText = ({
  html = "",
  searchText = "",
  maxChars = 300,
  partialSearch = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  // 🔒 Sanitize incoming HTML
  const safeHtml = useMemo(() => DOMPurify.sanitize(html || ""), [html]);

  // Remove tags for text length check
  const plainText = useMemo(
    () => safeHtml.replace(/<[^>]+>/g, "").trim(),
    [safeHtml]
  );

  const isOverflowing = plainText.length > maxChars;

  // ✅ Robust truncation that keeps HTML valid
  const truncatedHtml = useMemo(() => {
    if (!isOverflowing) return safeHtml;

    const parser = new DOMParser();
    const doc = parser.parseFromString(safeHtml, "text/html");
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);

    let count = 0;
    const toRemove = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const nodeText = node.nodeValue;
      const remaining = maxChars - count;

      if (remaining <= 0) {
        toRemove.push(node);
        continue;
      }

      if (nodeText.length > remaining) {
        node.nodeValue = nodeText.slice(0, remaining) + "...";
      }

      count += Math.min(nodeText.length, remaining);
    }

    toRemove.forEach((node) => node.parentNode?.removeChild(node));

    // 🧹 Clean up empty tags & extra line breaks
    let htmlOutput = doc.body.innerHTML;

    // 1️⃣ Remove empty generic tags
    htmlOutput = htmlOutput.replace(/<(\w+)[^>]*>\s*<\/\1>/g, "");

    // 2️⃣ Remove blank paragraphs
    htmlOutput = htmlOutput.replace(
      /<p[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi,
      ""
    );

    // 3️⃣ Collapse multiple <br>
    htmlOutput = htmlOutput.replace(/(<br\s*\/?>\s*){2,}/gi, "<br/>");

    // 4️⃣ Remove empty list items
    htmlOutput = htmlOutput.replace(
      /<li[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/li>/gi,
      ""
    );

    // 5️⃣ Remove empty lists
    htmlOutput = htmlOutput.replace(/<(ul|ol)[^>]*>\s*<\/\1>/gi, "");

    return htmlOutput;
  }, [safeHtml, maxChars, isOverflowing]);

  const toggleReadMore = () => setExpanded((prev) => !prev);

  const containerStyle = css`
    white-space: pre-line;
    word-break: break-word;
    cursor: default;

    p {
      margin: 0;
    }

    p:empty {
      display: none;
    }
  `;

  const displayHtml = expanded ? safeHtml : truncatedHtml;

  return (
    <div>
      <div css={containerStyle}>
        <HighlightedText
          text={displayHtml}
          searchText={searchText}
          partialSearch={partialSearch}
        />
      </div>

      {isOverflowing && (
        <span
          onClick={toggleReadMore}
          style={{
            color: "#1890ff",
            cursor: "pointer",
            display: "inline-block",
            marginTop: 4,
          }}
        >
          {expanded ? "Show less" : "Read more"}
        </span>
      )}
    </div>
  );
};

export default ReadMoreHtmlText;
